import React, { useEffect, useMemo, useRef, useCallback } from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@mui/styles'
import { useVirtualizer } from '@tanstack/react-virtual'
import ThumbnailListItem from '@/modules/file/components/thumbnail-list-item'
import { CssHelper } from '@/modules/base/view/mixin/helpers_mixin'
import { useAppSelector } from '@/redux/store'
import Loader from '@/modules/base/components/loader/loader'

const padding = 10

const useStyles = makeStyles(() => ({
  grid: {
    marginLeft: 'auto',
    paddingBottom: 30,
    display: 'grid',
  },
  item: {
    padding: CssHelper.px(30, 20, 30),
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minWidth: 0, // Allow items to shrink
    maxWidth: '100%', // Prevent overflow
  },
  gridWrapper: {
    height: '100%',
    width: '100%',
    scrollbarWidth: 'thin',
    overflow: 'auto',
    '&::-webkit-scrollbar, & *::-webkit-scrollbar': {
      display: 'none',
    },
    '&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb': {
      backgroundColor: '#ccc',
      borderRadius: 8,
      border: '2px solid white',
      '&:hover': {
        backgroundColor: '#aaa',
      },
    },
    '&:hover': {
      '&::-webkit-scrollbar, & *::-webkit-scrollbar': {
        display: 'unset',
      },
    },
  },
  loadingIndicator: {
    display: 'block',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '50px',
    gridColumn: '1 / -1',
    minHeight: '60px',
  },
  loader: {
    display: 'contents',
  },
}))

const GridWrapper = (props) => {
  const {
    width,
    height,
    onRefreshFiles,
    minSize,
    onPreviewRequest,
    onSelectFile,
    onDeleteFile,
    onAddTag,
    allowDrop,
    allowDrag,
    disableRightClickContextMenu,
  } = props

  const { pagination, items, isLoading } = useAppSelector(state => state.document.list)
  const { count } = pagination
  const offset = pagination.offset + pagination.limit
  const hasMore = offset < pagination.count

  const widthPreset = +minSize.split('px')[0]
  const itemWidth = widthPreset // Fixed item width to widthPreset
  const viewWidth = width - padding
  const columnCount = Math.max(1, Math.floor(viewWidth / widthPreset)) // Number of items per row based on widthPreset
  const itemHeight = (297 * widthPreset / 210) + 10

  const classes = useStyles(props)
  const parentRef = useRef()
  const bottomRef = useRef()

  const rowCount = Math.ceil(items.length / columnCount)

  // Memoize grid calculations to prevent unnecessary recalculations
  const gridConfig = useMemo(() => ({
    columnCount,
    itemWidth,
    itemHeight,
    rowCount,
    isSingleColumn: columnCount === 1,
  }), [columnCount, itemWidth, itemHeight, rowCount])

  const virtualizer = useVirtualizer({
    count: gridConfig.rowCount,
    getScrollElement: () => parentRef.current,
    estimateSize: () => gridConfig.itemHeight,
    overscan: 5,
  })

  // Force virtualizer to recalculate when grid config changes
  useEffect(() => {
    virtualizer.measure()
  }, [gridConfig, virtualizer])

  const loadMoreItems = useCallback(() => {
    if (isLoading || !hasMore) return
    onRefreshFiles({ offset })
  }, [hasMore, onRefreshFiles, isLoading, offset])

  // IntersectionObserver to trigger infinite scroll
  useEffect(() => {
    if (!bottomRef.current || !hasMore) return
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && hasMore && !isLoading) {
        loadMoreItems()
      }
    }, {
      root: null, // Use viewport instead of container
      rootMargin: '200px', // Start loading 200px before reaching the bottom
      threshold: 0.1,
    })

    if (bottomRef.current) observer.observe(bottomRef.current)

    return () => observer.disconnect()
  }, [loadMoreItems, hasMore, isLoading])

  return (
    <div
      ref={parentRef}
      className={classes.gridWrapper}
      style={{ width, height }}
      key={`grid-${gridConfig.columnCount}-${gridConfig.itemWidth}`}
    >
      {items.length > 0 && (
        <>
          <div
            className={classes.grid}
            style={{
              height: `${virtualizer.getTotalSize()}px`,
              position: 'relative',
            }}
          >
            {virtualizer.getVirtualItems().map(virtualRow => {
              const startIndex = virtualRow.index * gridConfig.columnCount
              return (
                <div
                  key={virtualRow.key}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    transform: `translateY(${virtualRow.start}px)`,
                    display: 'grid',
                    gridTemplateColumns: gridConfig.isSingleColumn
                      ? '1fr'
                      : `repeat(${gridConfig.columnCount}, ${gridConfig.itemWidth}px)`,
                    width: '100%',
                    height: gridConfig.itemHeight,
                    justifyContent: gridConfig.isSingleColumn ? 'center' : 'space-around',
                  }}
                >
                  {Array.from({ length: gridConfig.columnCount }).map((_, colIdx) => {
                    const index = startIndex + colIdx
                    const file = items[index]
                    const itemKey = file?.id ?? file?.uploadToken ?? file?.minioId ?? index
                    return file
                      ? (
                        <div
                          key={itemKey}
                          className={classes.item}
                          style={{
                            width: gridConfig.isSingleColumn ? gridConfig.itemWidth : 'auto',
                            maxWidth: gridConfig.itemWidth,
                          }}
                        >
                          <ThumbnailListItem
                            {...props}
                            file={file}
                            disableRightClickContextMenu={disableRightClickContextMenu}
                            onPreviewRequest={onPreviewRequest}
                            onSelectFile={onSelectFile}
                            onDeleteFile={onDeleteFile}
                            onAddTag={onAddTag}
                            allowDrop={allowDrop}
                            allowDrag={allowDrag}
                          />
                        </div>
                      )
                      : null
                  })}
                </div>
              )
            })}
          </div>

          {/* Sentinel to trigger loading more */}
          <div
            ref={bottomRef}
            className={classes.loadingIndicator}
          >
            <Loader className={classes.loader} visible={items.length < count} />
          </div>
        </>
      )}
    </div>
  )
}

GridWrapper.propTypes = {
  width: PropTypes.number.isRequired,
  height: PropTypes.number.isRequired,
  onSelectFile: PropTypes.func,
  onPreviewRequest: PropTypes.func.isRequired,
  onRefreshFiles: PropTypes.func.isRequired,
  onDeleteFile: PropTypes.func,
  onAddTag: PropTypes.func,
  className: PropTypes.string,
  minSize: PropTypes.string,
  allowDrop: PropTypes.bool,
  allowDrag: PropTypes.bool,
  disableFavButton: PropTypes.bool,
  disableDeleteButton: PropTypes.bool,
  disableRightClickContextMenu: PropTypes.bool,
}

export default GridWrapper
